import React, { useState, useEffect } from 'react';
import { Label } from './ui/label';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentsStep = ({ formData, draftToken }) => {
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('other');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/document-categories`)
      .then(res => setCategories(res.data.categories))
      .catch(() => {});
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', selectedCategory);

      const response = await axios.post(
        `${BACKEND_URL}/api/application/${draftToken}/document`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.status === 'success') {
        const catLabel = categories.find(c => c.key === selectedCategory)?.label || selectedCategory;
        setDocuments(prev => [...prev, {
          id: response.data.documentId,
          filename: response.data.filename,
          category: catLabel,
        }]);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setUploadError('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-slate-800 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-slate-100 mb-6">Documents</h3>
        <p className="text-slate-300 mb-6">Please upload required documents such as business license, bank statements, ID, etc.</p>

        <div className="space-y-6">
          <div>
            <Label className="text-slate-200 mb-3 block">Document Type</Label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              {categories.map(cat => (
                <option key={cat.key} value={cat.key}>{cat.label}</option>
              ))}
              {categories.length === 0 && <option value="other">Other</option>}
            </select>
          </div>

          <div>
            <Label className="text-slate-200 mb-3 block">Upload Document</Label>
            <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-slate-500 transition-colors">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleFileUpload}
                disabled={uploading}
                accept=".jpg,.jpeg,.png,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.msg,.heic,.heif"
                data-testid="file-upload-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex flex-col items-center">
                  <svg className="w-12 h-12 text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-slate-300 mb-2">
                    {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-sm text-slate-500">PDF, PNG, JPG, DOC, XLS up to 10MB</p>
                </div>
              </label>
            </div>
            {uploadError && (
              <p className="text-red-400 mt-2 text-sm">{uploadError}</p>
            )}
          </div>

          {documents.length > 0 && (
            <div>
              <Label className="text-slate-200 mb-3 block">Uploaded Documents</Label>
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div key={doc.id} className="bg-slate-900 p-4 rounded-lg flex items-center justify-between" data-testid={`document-${doc.id}`}>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <span className="text-slate-200">{doc.filename}</span>
                        <span className="text-slate-500 text-sm ml-3">{doc.category}</span>
                      </div>
                    </div>
                    <span className="text-green-400 text-sm">Uploaded</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-slate-800 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-slate-200 mb-4">Required Documents</h4>
        <ul className="space-y-2 text-slate-300">
          <li className="flex items-start">
            <span className="mr-2">&bull;</span>
            <span>Corporation documents (business license or formation documents)</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">&bull;</span>
            <span>Voided check or bank letter</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">&bull;</span>
            <span>Driver's license for all principals</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">&bull;</span>
            <span>Bank statements (last 3 months)</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">&bull;</span>
            <span>Processing statements (if currently processing)</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DocumentsStep;
